import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  CompareTableVersionsRequest,
  ResultCompareComparisonEntry,
  ResultCompareConsistency,
  ResultCompareEnrichedResponse,
  ResultCompareJobSummary,
  ResultCompareService,
} from './result-compare.service';

/** Optional document file info for clipboard (Job ID, Score, Quality, Summary). */
export interface DocumentClipboardFileInfo {
  jobId?: string;
  score?: number;
  quality?: string;
  summary?: {
    total_fields?: number;
    fields_with_value?: number;
    fields_empty?: number;
  };
}

export interface DocumentQaSummary {
  jobId: string;
  scopeLabel: string;
  consistency: ResultCompareConsistency | null;
  comparisons: ResultCompareComparisonEntry[];
}

@Injectable({
  providedIn: 'root',
})
export class QaSummaryHelper {
  constructor(private resultCompareService: ResultCompareService) {}

  async getDocumentQaSummary(params: {
    tableId: string;
    fileId: string;
    currentJobId: string;
  }): Promise<DocumentQaSummary> {
    const { tableId, fileId, currentJobId } = params;
    if (!currentJobId || !fileId || !tableId) {
      throw new Error('QA summary is unavailable for this document.');
    }

    const jobsResponse = await firstValueFrom(
      this.resultCompareService.getTableJobs(tableId, 200, 0)
    );

    const allJobs = this.getJobsFromResponse(jobsResponse).filter(
      (job) => job.hasResult !== false
    );
    const fileScopedJobs = allJobs.filter((job) => job.fileId === fileId);
    const jobs = (fileScopedJobs.length ? fileScopedJobs : allJobs).sort(
      (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
    );

    if (!jobs.length) {
      throw new Error('No prior runs found for this document.');
    }

    const currentIndex = jobs.findIndex((job) => job.jobId === currentJobId);
    const currentJob = currentIndex >= 0 ? jobs[currentIndex] : jobs[0];
    const previousJob =
      currentIndex >= 0 ? jobs[currentIndex + 1] : jobs[1];

    if (!previousJob?.jobId) {
      throw new Error('No previous run to compare.');
    }

    const comparisonResponse = await firstValueFrom(
      this.resultCompareService.compareByJobs([
        previousJob.jobId,
        currentJob.jobId,
      ])
    );

    const comparison = comparisonResponse?.comparison;
    return {
      jobId: currentJob.jobId,
      scopeLabel: 'current run vs previous run',
      consistency: comparison?.consistency ?? null,
      comparisons: comparison?.comparisons ?? [],
    };
  }

  /**
   * Compare the two most recent batches for a table (dataset comparison by batch ID).
   * Returns the enriched comparison response or null if fewer than 2 batches.
   */
  async getDatasetBatchComparison(tableId: string): Promise<ResultCompareEnrichedResponse | null> {
    if (!tableId) {
      throw new Error('Table ID is required for dataset comparison.');
    }

    const batchesResponse = await firstValueFrom(
      this.resultCompareService.getTableBatches(tableId, 50, 0)
    );

    const batches = batchesResponse?.batches ?? [];
    if (batches.length < 2) {
      return null;
    }

    const previousBatchId = batches[1].batchId;
    const currentBatchId = batches[0].batchId;

    const response = await firstValueFrom(
      this.resultCompareService.compareByBatches([previousBatchId, currentBatchId])
    );

    return response ?? null;
  }

  /**
   * Fetch dataset batch comparison and log the result to the console (for Dataset tab).
   */
  async fetchDatasetBatchComparisonAndLog(tableId: string): Promise<void> {
    try {
      const result = await this.getDatasetBatchComparison(tableId);
      console.log('[QA Dataset] Batch comparison result:', result);
    } catch (error) {
      console.error('[QA Dataset] Batch comparison failed:', error);
    }
  }

  /**
   * Compare table versions (schema comparison) for Data Fields tab.
   * Calls POST result-compare/table-versions with tableId and optional schemaVersions, lastNBatches, fileName.
   */
  async getSchemaTableVersionsComparison(
    tableId: string,
    options?: { schemaVersions?: number[]; lastNBatches?: number; fileName?: string }
  ): Promise<ResultCompareEnrichedResponse | null> {
    if (!tableId) {
      throw new Error('Table ID is required for schema comparison.');
    }

    const body: CompareTableVersionsRequest = { tableId };
    if (options?.schemaVersions?.length) body.schemaVersions = options.schemaVersions;
    if (options?.lastNBatches != null) body.lastNBatches = options.lastNBatches;
    if (options?.fileName != null) body.fileName = options.fileName;

    try {
      const response = await firstValueFrom(
        this.resultCompareService.compareTableVersions(body)
      );
      return response ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Fetch schema (table versions) comparison and log the result to the console (for Data Fields tab).
   */
  async fetchSchemaComparisonAndLog(tableId: string): Promise<void> {
    try {
      const result = await this.getSchemaTableVersionsComparison(tableId);
      console.log('[QA Data Fields] Schema comparison result:', result);
    } catch (error) {
      console.error('[QA Data Fields] Schema comparison failed:', error);
    }
  }

  buildDocumentClipboardText(
    summary: DocumentQaSummary,
    documentInfo?: DocumentClipboardFileInfo | null
  ): string {
    const info = documentInfo ?? null;
    const summaryLine =
      info?.summary != null
        ? `${info.summary.total_fields ?? '—'} fields, ${info.summary.fields_with_value ?? '—'} with value, ${info.summary.fields_empty ?? '—'} empty`
        : '—';

    const lines: string[] = [];
    lines.push('QA - Single Document');
    lines.push(`Job ID: ${info?.jobId ?? summary.jobId ?? '—'}`);
    lines.push(`Score: ${info?.score ?? '—'}`);
    lines.push(`Quality: ${info?.quality ?? '—'}`);
    lines.push(`Summary: ${summaryLine}`);
    lines.push('');
    lines.push('Consistency');
    const c = summary.consistency;
    if (c) {
      lines.push(`Overall: ${c.overall_percentage ?? '—'}%`);
      lines.push(`Level: ${c.level ?? '—'}`);
      lines.push(`Quality: ${c.quality ?? '—'}`);
      lines.push(`Total paths: ${c.total_paths ?? '—'}`);
      if (c.penalty_breakdown) {
        const pb = c.penalty_breakdown;
        lines.push(`Penalty breakdown: all_match ${pb.all_match ?? '—'}, value_different ${pb.value_different ?? '—'}, missing_key ${pb.missing_key ?? '—'}`);
      }
    } else {
      lines.push('—');
    }
    lines.push('');
    lines.push('Comparisons');
    lines.push(
      summary.comparisons?.length
        ? JSON.stringify(summary.comparisons, null, 2)
        : '—'
    );

    return lines.join('\n');
  }

  private getJobsFromResponse(response: unknown): ResultCompareJobSummary[] {
    const res = response as { jobs?: ResultCompareJobSummary[] };
    if (!res?.jobs || !Array.isArray(res.jobs)) {
      return [];
    }
    return res.jobs;
  }
}
